// client/src/pages/sections/groups/MoveSgToRgButton.js
// "Move this Signer Group to another Relationship Group" affordance shown
// on each non-anchor Signer Group card inside the RG composition
// (RgCompareCards) when the RG is being reviewed in the
// "Relationship Group needs partner confirmation" step (Step 4).
//
// Unlike MoveToRgButton (which re-parents a single client), this moves an
// ENTIRE Signer Group between Relationship Groups via the move_sg_to_rg
// verb. The server stamps both the source and destination RGs
// 'partner_moved' so each re-surfaces for confirmation, and revokes the
// partner's Step 4 attestation if they had already signed off.

import { useEffect, useState } from 'react';
import { loadAnchors, getAnchors } from './clientRosterCache';
import GroupAnchorPicker from './GroupAnchorPicker';
import { emitToast, errorText } from './toast';
import { postResolveAction } from './resolveActionQueue';
import ClientChip from './ClientChip';
import { useSubmitGuard } from './useSubmitGuard';

const CODE_RE = /(\d{5}-\d{3})/;

// Composition patch: the Signer Group leaves this Relationship Group.
function removeSgPatch(sgCode) {
  return (prev) => {
    const gone = (prev.sgs || []).find(x => x.sgCode === sgCode);
    return {
      ...prev,
      sgs: (prev.sgs || []).filter(x => x.sgCode !== sgCode),
      totalMembers: Math.max(0, (prev.totalMembers || 0) - ((gone && gone.members.length) || 0)),
    };
  };
}

export default function MoveSgToRgButton({ sg, currentRgAnchor, mutate, testMode, testSessionId, allowMakeOwn = true }) {
  const sgCode = sg.sgCode;
  const sgName = sg.sgName;
  const guard  = useSubmitGuard();   // one submit at a time: a double-click or a re-click mid-wait is ignored
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [rgAnchors, setRgAnchors] = useState(() => (getAnchors()?.rgAnchors) || []);
  const [choice, setChoice]     = useState('existing');   // 'existing' | 'self'
  const [manualRg, setManualRg] = useState('');
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadAnchors({ fresh: true })   // not the copy from page load: earlier steps change which clients anchor a group
      .then((anchorsRes) => { if (!cancelled) setRgAnchors(anchorsRes.rgAnchors || []); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  function close() {
    setOpen(false);
    setChoice('existing');
    setManualRg('');
    setError(null);
  }

  // The "move to an existing Relationship Group" picker must exclude this
  // Signer Group's CURRENT Relationship Group (moving it there is a no-op /
  // self-reference) and the Signer Group's own code (that's the "make its own"
  // option, not a move). The submit() guards stay as a backstop.
  const rgOptions = (rgAnchors || []).filter(
    a => a.clientCode !== currentRgAnchor && a.clientCode !== sgCode
  );

  function submit() {
    let payload;
    if (choice === 'self') {
      payload = { makeOwnRg: true };
    } else {
      const codeMatch = manualRg.match(CODE_RE);
      if (!codeMatch) {
        setError('Pick the destination Relationship Group from the list.');
        return;
      }
      const target = codeMatch[1];
      if (target === sgCode) {
        setError('That is this Signer Group itself — choose “its own Relationship Group” instead.');
        return;
      }
      if (currentRgAnchor && target === currentRgAnchor) {
        setError('This Signer Group is already in that Relationship Group.');
        return;
      }
      payload = { targetRg: target };
    }

    const body = {
      verb: 'move_sg_to_rg',
      clientCode: sgCode,            // the Signer Group anchor
      payload,
      testMode: Boolean(testMode),
      sessionId: testMode ? testSessionId : null,
    };

    // Optimistic: close the dialog and take the Signer Group out of this Relationship
    // Group's composition right away; the write runs in the background. RgCompareCards'
    // `mutate` reverts the composition and rejects if the server refuses.
    const label = payload.makeOwnRg ? 'create a new Relationship Group for' : 'move';
    close();
    mutate({ patch: removeSgPatch(sgCode), task: () => postResolveAction(body), reload: true })
      .then((data) => {
        emitToast({
          kind: 'success',
          message: (data && data.message) ||
            `Signer Group ${sgCode} moved. Relationship Groups now need re-confirmation.`,
        });
      })
      .catch((e) => {
        emitToast({ kind: 'error', message: `Couldn't ${label} Signer Group ${sgCode}: ${errorText(e)}. Restored.` });
      });
  }

  return (
    <>
      <button
        type="button"
        className="cg-move-btn"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={`Move Signer Group ${sgCode} into a different Relationship Group, or make it its own.`}
      >
        Move / new Relationship Group
      </button>

      {open && (
        <div
          className="cg-resolve-overlay"
          onClick={close}
          role="dialog"
          aria-modal="true"
        >
          <div className="cg-resolve-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cg-resolve-header">
              <h3>Move Signer Group {sgCode} to a different Relationship Group</h3>
              <button
                type="button"
                className="cg-resolve-close"
                onClick={close}
                aria-label="Close"
              >×</button>
            </div>
            <div className="cg-resolve-summary">
              <div className="cg-resolve-summary-row">
                <ClientChip code={sgCode} name={sgName} />
              </div>
            </div>

            <div className="cg-resolve-confirm">
              <p className="cg-resolve-confirm-body">
                Every client in this Signer Group moves together. The current and
                destination Relationship Groups will need to be re-confirmed afterward.
              </p>

              {loading ? (
                <p className="cg-resolve-empty">Loading Relationship Groups…</p>
              ) : (
                <div className="cg-resolve-form">
                  <fieldset className="cg-resolve-field cg-resolve-field--radio">
                    <legend className="cg-resolve-field-label">Where should this Signer Group go?</legend>
                    <label className="cg-resolve-radio-row">
                      <input
                        type="radio"
                        name="moveSgChoice"
                        value="existing"
                        checked={choice === 'existing'}
                        onChange={() => setChoice('existing')}
                      />
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        Move to existing Relationship Group anchored by
                        <GroupAnchorPicker
                          anchors={rgOptions}
                          value={manualRg}
                          disabled={choice !== 'existing'}
                          onChange={(v) => { setManualRg(v); setChoice('existing'); }}
                          onFocus={() => setChoice('existing')}
                        />
                      </span>
                    </label>
                    {/* "Create a new Relationship Group" is meaningless when
                        this is the only signer group AND it has a single client
                        — it's already effectively its own group, so the option
                        is hidden (see allowMakeOwn). */}
                    {allowMakeOwn && (
                      <label className="cg-resolve-radio-row">
                        <input
                          type="radio"
                          name="moveSgChoice"
                          value="self"
                          checked={choice === 'self'}
                          onChange={() => setChoice('self')}
                        />
                        <span>Create a new Relationship Group (this Signer Group becomes its own)</span>
                      </label>
                    )}
                  </fieldset>
                </div>
              )}

              {error && (
                <div className="cg-resolve-error" role="alert">
                  <strong>Couldn't move:</strong> {error}
                </div>
              )}

              <div className="cg-resolve-actions">
                <button
                  type="button"
                  className="cg-btn cg-btn--secondary"
                  onClick={close}
                >Cancel</button>
                <button
                  type="button"
                  className="cg-btn cg-btn--primary"
                  disabled={loading}
                  onClick={() => guard(submit)}
                >
                  {choice === 'self'
                    ? 'Create new Relationship Group'
                    : 'Move Signer Group'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
